/**
 * Asta Cafe bespoke chrome — utility bar + sticky gold header + footer, wrapping
 * the landing. This is CONTENT-FIRST (a marketing page, not a shop), so — unlike
 * agp's shell — it renders NO cart / search / account: the header is in-page
 * anchors throughout, including the gold "Where to Buy" pill, which scrolls to
 * the Contact band rather than routing anywhere.
 *
 * The SHARED green <Header/> is deliberately NOT used: it's marketplace chrome
 * driven by the (green) tenant theme, which would clash with this gold/espresso
 * page. The shop flow (cart/checkout/product) keeps the shared header on its own
 * routes; only the public landing owns this chrome.
 */
import { type ReactNode, type MouseEvent, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SOCIALPRENEUR_SITE } from '@mlm/shared';
import { useSiteContent } from '../../../storefront/lib/site-content';
import { useCommerceEnabled } from '../../../storefront/lib/hooks';
import { isCommercePath } from '@mlm/shared';
import { NAV, FOOTER_COLS, LOGO, FONT_DISPLAY } from './tokens';
import './asta.css';

/** Honour the OS "reduce motion" setting — this chrome routes almost all of its
 * navigation through scrolling, so a long smooth scroll is the main motion on
 * the site for someone who asked not to have any. */
const scrollBehavior = (): ScrollBehavior =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

/** Smooth-scroll to an in-page anchor (mirrors mhyel's scrollTo helper). Route
 * hrefs ('/...') are handled by <Link>, so this only fires for '#...' anchors. */
export const scrollToAnchor = (href: string) => (e: MouseEvent) => {
  if (!href.startsWith('#')) return;
  e.preventDefault();
  const id = href.slice(1) || 'top';
  const behavior = scrollBehavior();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior, block: 'start' });
  else if (id === 'top') window.scrollTo({ top: 0, behavior });
};

/**
 * A chrome link: react-router <Link> for routes, smooth-scroll <a> for anchors.
 *
 * The chrome is shared by the landing and the bespoke /about page, and every
 * nav item is an anchor into a LANDING section. Off the landing those elements
 * don't exist, so scrolling would silently do nothing — the anchor has to
 * navigate home first and scroll on arrival. That hand-off rides router state
 * (`scrollTo`) rather than a `/#id` URL, because in an SPA the browser's native
 * hash scroll fires before React has painted the target section.
 */
function ChromeLink({
  href,
  className,
  style,
  children,
}: {
  href: string;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}) {
  const onLanding = useLocation().pathname === '/';
  if (href.startsWith('/')) {
    const [path, hash] = href.split('#');
    // No hash means "start at the top". The member SPA has no scroll
    // restoration, so without an explicit instruction the browser keeps the
    // current offset and a click from deep in the footer lands the visitor
    // part-way down the destination with its header scrolled off.
    // The hash is kept in the URL (not just in router state) so the link is
    // still meaningful when copied or opened in a new tab.
    return (
      <Link
        to={{ pathname: path!, hash: hash ? `#${hash}` : '' }}
        state={{ scrollTo: hash || 'top' }}
        className={className}
        style={style}
      >
        {children}
      </Link>
    );
  }
  if (!onLanding) {
    // A real `/#section` href, so the link survives middle-click, copy-link and
    // open-in-new-tab; the router state is the in-SPA fast path, and the hash is
    // the fallback `useScrollToStateTarget` reads on a cold load.
    return (
      <Link
        to={{ pathname: '/', hash: href }}
        state={{ scrollTo: href.slice(1) || 'top' }}
        className={className}
        style={style}
      >
        {children}
      </Link>
    );
  }
  return (
    <a href={href} onClick={scrollToAnchor(href)} className={className} style={style}>
      {children}
    </a>
  );
}

/**
 * Consume the `scrollTo` router state left by a cross-page ChromeLink and jump
 * to that section once the destination page has painted. Used by BOTH bespoke
 * pages (the landing for its sections, /about for `#mission`).
 */
export function useScrollToStateTarget(): void {
  const { state, key, hash } = useLocation();
  // Router state is the in-SPA path; the URL hash covers a cold load (someone
  // opened `/#blend` in a new tab), where the browser's own hash scroll fires
  // before React has painted the section.
  const target = (state as { scrollTo?: string } | null)?.scrollTo ?? (hash ? hash.slice(1) : '');
  useEffect(() => {
    if (!target) return;
    // rAF so the section exists in the DOM before we measure it.
    const raf = requestAnimationFrame(() => {
      const behavior = scrollBehavior();
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior, block: 'start' });
      else if (target === 'top') window.scrollTo({ top: 0, behavior });
    });
    return () => cancelAnimationFrame(raf);
    // `key` is in the deps so re-clicking the SAME link from the page it
    // already points at scrolls again — the target alone never changes, so the
    // effect would otherwise fire once and go quiet.
  }, [target, key]);
}

/** The gold shield logo — a transparent PNG, so it sits directly on the dark
 * chrome with a soft drop-shadow for separation (no plate needed). */
function LogoPlate({ size = 48 }: { size?: number }) {
  return (
    <img
      src={LOGO}
      alt="Asta Cafe — a SocialPreneur Inc. brand"
      style={{
        height: size,
        width: 'auto',
        display: 'block',
        flex: 'none',
        filter: 'drop-shadow(0 4px 10px rgba(0,0,0,.45))',
      }}
    />
  );
}

export function AstaShell({ children }: { children: ReactNode }) {
  const sc = useSiteContent(SOCIALPRENEUR_SITE);
  // Handles the cross-page anchor hand-off for every page wearing this chrome.
  useScrollToStateTarget();
  // The Products carousel section only exists once the client adds slides via
  // Site Pages — its nav anchor appears/disappears with it.
  const hasProducts = sc.items('landing.products.slides').length > 0;
  // Same rule for the FAQ: an emptied-out question list means no section to
  // scroll to, so the anchor must go with it.
  const hasFaq = sc.items('landing.faq.items').length > 0;
  const nav = [
    ...NAV.slice(0, 1),
    ...(hasProducts ? [{ label: 'Products', href: '#products' }] : []),
    ...NAV.slice(1),
    ...(hasFaq ? [{ label: 'FAQ', href: '#faq' }] : []),
  ];
  // FOOTER_COLS holds in-page anchors plus the bespoke /about route, so nothing
  // here dead-ends. Two filters still apply: drop the FAQ link when the client
  // has emptied the question list, and keep the info-only guard so that if
  // anyone ever adds a SHOP route back into this chrome it can't dead-end on
  // the catch-all redirect when the commerce routes are unmounted.
  const commerce = useCommerceEnabled();
  const footerCols = FOOTER_COLS.map((c) => ({
    ...c,
    links: c.links.filter(
      (l) => (commerce || !isCommercePath(l.href)) && (hasFaq || l.href !== '#faq'),
    ),
  })).filter((c) => c.links.length > 0);
  return (
    <div id="top" className="asta-root">
      {/* ── Utility bar ── */}
      <div style={{ background: 'var(--chrome)', borderBottom: '1px solid var(--line)' }}>
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '0 24px',
            minHeight: 38,
            display: 'flex',
            alignItems: 'center',
            gap: 18,
            flexWrap: 'wrap',
            fontSize: 12,
            letterSpacing: '.02em',
            color: 'var(--muted)',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0' }}>
            <i style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--gold)', display: 'block' }} />
            Empowering Communities — a SocialPreneur Inc. brand
          </span>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
            <span style={{ color: 'var(--gold-l)', fontWeight: 700, letterSpacing: '.14em', fontFamily: FONT_DISPLAY }}>
              HALAL CERTIFIED
            </span>
            <i style={{ width: 1, height: 12, background: 'var(--line)', display: 'block' }} />
            <span style={{ color: 'var(--gold-l)', fontWeight: 700, letterSpacing: '.14em', fontFamily: FONT_DISPLAY }}>
              NAXA VERIFIED
            </span>
          </div>
        </div>
      </div>

      {/* ── Header ── */}
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          background: 'rgba(18,12,6,.78)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderBottom: '1px solid var(--line)',
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '12px 24px',
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            flexWrap: 'wrap',
          }}
        >
          <ChromeLink href="#top" style={{ display: 'flex', alignItems: 'center', flex: 'none' }}>
            <LogoPlate />
          </ChromeLink>
          <nav style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 26, flexWrap: 'wrap' }}>
            {nav.map((n) => (
              <ChromeLink
                key={n.href}
                href={n.href}
                className="asta-navlink"
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 500,
                  fontSize: 14,
                  letterSpacing: '.08em',
                  textTransform: 'uppercase',
                  color: 'var(--cream)',
                  opacity: 0.86,
                }}
              >
                {n.label}
              </ChromeLink>
            ))}
          </nav>
          {/* The header CTA is an in-page scroll to the Contact band, NOT a route
              into the shop: this tenant is an information site, so "Where to Buy"
              means "talk to us", and the button must work whether or not the
              commerce routes happen to be mounted. Label is client-editable. */}
          <ChromeLink
            href="#contact"
            className="asta-btn-gold"
            style={{
              flex: 'none',
              fontFamily: FONT_DISPLAY,
              fontWeight: 600,
              fontSize: 13.5,
              letterSpacing: '.1em',
              textTransform: 'uppercase',
              color: '#2a1b0e',
              background: 'var(--gold-grad)',
              padding: '11px 20px',
              borderRadius: 9,
              boxShadow: '0 4px 14px rgba(200,148,42,.3)',
            }}
          >
            {sc.text('landing.cta.ctaPrimary')}
          </ChromeLink>
        </div>
      </header>

      <main>{children}</main>

      {/* ── Footer ── */}
      <footer style={{ background: 'var(--chrome)', borderTop: '1px solid var(--line)', color: 'var(--muted)' }}>
        <div
          style={{
            maxWidth: 1200,
            margin: '0 auto',
            padding: '56px 24px 28px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit,minmax(210px,1fr))',
            gap: 36,
          }}
        >
          <div style={{ minWidth: 240 }}>
            <LogoPlate size={42} />
            <p style={{ fontSize: 13.5, lineHeight: 1.7, margin: '16px 0 0', maxWidth: '34ch' }}>
              A premium wellness coffee with inulin &amp; astaxanthin — a SocialPreneur Inc. brand. Empowering
              Communities.
            </p>
          </div>
          {footerCols.map((col) => (
            <div key={col.title}>
              <h4
                style={{
                  fontFamily: FONT_DISPLAY,
                  fontWeight: 600,
                  fontSize: 12,
                  letterSpacing: '.14em',
                  textTransform: 'uppercase',
                  color: 'var(--cream)',
                  margin: '0 0 16px',
                }}
              >
                {col.title}
              </h4>
              {col.links.map((l) => (
                <ChromeLink
                  key={l.label}
                  href={l.href}
                  className="asta-flink"
                  style={{ display: 'block', fontSize: 13.5, color: 'var(--muted)', marginBottom: 10 }}
                >
                  {l.label}
                </ChromeLink>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: '1px solid var(--line)' }}>
          <div
            style={{
              maxWidth: 1200,
              margin: '0 auto',
              padding: '20px 24px',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
              justifyContent: 'space-between',
              fontSize: 12,
              color: 'var(--muted-2)',
            }}
          >
            <span>© 2026 SocialPreneur Inc. · Powered by Omni Tech Business IT Solutions</span>
            <span>*This product is not intended to diagnose, treat, cure, or prevent any disease.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
